
import Vue from 'vue';

import CustomerDataService from '@root/modules/customer/services/CustomerData.service';
import { Tag } from '@root/common/types/fragment';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { faGift } from '@delfi.lt/fa-kit/icons/modules/classic/solid';
import { faTimes } from '@delfi.lt/fa-kit/icons/modules/classic/light';

const teliaProjectTags = [96617035];
const localStorageKey = 'telia_project_reads';
const localStorageClearInterval = 1000 * 60 * 60 * 24;
const tagMaxArticles: Record<number, number> = {
  96617035: 10,
};

interface LocalStorageData {
  createdAt: number;
  byKey: Record<string, number[]>;
}

interface Props {
  article: ArticleData;
}

interface Data {
  customerDataService: CustomerDataService | null;
  isVisible: boolean;
  readArticleIds: number[];
}

interface Computed {
  customerDataKey: string | null;
  customerToken: string;
  progressPercentage: number;
  isRegisteredUser: boolean;
  readArticlesCount: number;
  teliaProjectTag?: Tag;
  totalArticles: number;
}

interface Methods {
  createEmptyStorageData: () => LocalStorageData;
  getStoredLocalData: () => LocalStorageData;
  getRegisteredReadArticleIds: () => Promise<number[]>;
  getAnonymousReadArticleIds: () => number[];
  normalizeArticleIds: (ids: number[]) => number[];
  loadReadArticleIds: () => Promise<void>;
  storeAnonymousReadArticleId: () => void;
  storeRegisteredReadArticleId: () => Promise<void>;
  syncCustomerDataToken: () => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      customerDataService: null,
      faGift,
      faTimes,
      isVisible: true,
      readArticleIds: [],
    };
  },
  computed: {
    customerToken() {
      return this.$store.state.piano.token;
    },
    isRegisteredUser() {
      return !!this.customerToken;
    },
    teliaProjectTag() {
      return this.article.tags.items.find((tag) => teliaProjectTags.includes(tag.id));
    },
    totalArticles() {
      if (!this.teliaProjectTag) {
        return 0;
      }

      return tagMaxArticles[this.teliaProjectTag.id] || 0;
    },
    readArticlesCount() {
      if (!this.totalArticles) {
        return this.readArticleIds.length;
      }

      return Math.min(this.readArticleIds.length, this.totalArticles);
    },
    progressPercentage() {
      if (!this.totalArticles) {
        return 0;
      }

      return Math.min((this.readArticlesCount / this.totalArticles) * 100, 100);
    },
    customerDataKey() {
      if (!this.teliaProjectTag) {
        return null;
      }

      return `telia-project-read-article--${this.teliaProjectTag.id}`;
    },
  },
  watch: {
    async customerToken() {
      this.syncCustomerDataToken();
      await this.loadReadArticleIds();
    },
    async customerDataKey() {
      await this.loadReadArticleIds();
    },
    async article() {
      await this.loadReadArticleIds();
    },
  },
  async mounted() {
    this.customerDataService = new CustomerDataService();
    this.syncCustomerDataToken();
    await this.loadReadArticleIds();
  },
  methods: {
    syncCustomerDataToken() {
      if (!this.customerDataService) {
        return;
      }

      this.customerDataService.setCustomerToken(this.customerToken);
    },
    createEmptyStorageData() {
      return {
        createdAt: Date.now(),
        byKey: {},
      };
    },
    normalizeArticleIds(ids) {
      return [...new Set(ids.filter((id) => Number.isFinite(id)))];
    },
    getStoredLocalData() {
      if (!process.client) {
        return this.createEmptyStorageData();
      }

      const storageData = window.localStorage.getItem(localStorageKey);

      if (!storageData) {
        return this.createEmptyStorageData();
      }

      try {
        const parsedStorageData = JSON.parse(storageData) as LocalStorageData;

        if (Date.now() - parsedStorageData.createdAt >= localStorageClearInterval) {
          return this.createEmptyStorageData();
        }

        return {
          createdAt: parsedStorageData.createdAt || Date.now(),
          byKey: parsedStorageData.byKey || {},
        };
      } catch (error) {
        return this.createEmptyStorageData();
      }
    },
    getAnonymousReadArticleIds() {
      if (!this.customerDataKey) {
        return [];
      }

      const storageData = this.getStoredLocalData();
      return this.normalizeArticleIds(storageData.byKey[this.customerDataKey] || []);
    },
    storeAnonymousReadArticleId() {
      if (!process.client || this.isRegisteredUser || !this.customerDataKey || !Number.isFinite(this.article.id)) {
        return;
      }

      const storageData = this.getStoredLocalData();
      const existingIds = storageData.byKey[this.customerDataKey] || [];
      const nextIds = this.normalizeArticleIds([...existingIds, Number(this.article.id)]);

      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          createdAt: Date.now(),
          byKey: {
            ...storageData.byKey,
            [this.customerDataKey]: nextIds,
          },
        })
      );
    },
    async getRegisteredReadArticleIds() {
      if (!this.customerDataService || !this.customerDataKey || !this.customerToken) {
        return [];
      }

      const response = await this.customerDataService.get({
        key: [this.customerDataKey],
        limit: 100,
        offset: 0,
      });

      const itemValues = response?.data?.items?.map((item) => Number(item.value)) || [];

      return this.normalizeArticleIds(itemValues);
    },
    async storeRegisteredReadArticleId() {
      if (!this.customerDataService || !this.customerDataKey || !this.customerToken || !Number.isFinite(this.article.id)) {
        return;
      }

      const articleId = String(this.article.id);
      const existingEntry = await this.customerDataService.get({
        key: [this.customerDataKey],
        value: articleId,
        limit: 1,
        offset: 0,
      });

      if (existingEntry?.data?.items?.length) {
        return;
      }

      await this.customerDataService.create({
        key: this.customerDataKey,
        value: articleId,
      });
    },
    async loadReadArticleIds() {
      if (!this.teliaProjectTag) {
        this.readArticleIds = [];
        return;
      }

      try {
        if (this.isRegisteredUser) {
          await this.storeRegisteredReadArticleId();
        } else {
          this.storeAnonymousReadArticleId();
        }

        this.readArticleIds = this.isRegisteredUser ? await this.getRegisteredReadArticleIds() : this.getAnonymousReadArticleIds();
      } catch (e) {
        this.$sentry.captureException(e, {
          contexts: { vue: { componentName: 'ArticleTeliaProjectScroller', method: 'getReadArticleIds' } },
          tags: { 'process.type': process.server ? 'server' : 'client' },
        });
      }
    },
  },
});
